import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Space, Typography, Popconfirm, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { FaVault } from 'react-icons/fa6';
import { BsFillBinocularsFill } from 'react-icons/bs';
import EntityManagementScreen from '../common/EntityManagementScreen';
import VaultSubStudyManagementDrawer from '../../components/identifierVault/VaultSubStudyManagementDrawer.component';
import { backfillStudyHashes } from '../../actions/identifierVaultActions';
import { VAULT_PATIENT_BACKFILL_RESET } from '../../constants/identifierVaultConstants';
import { showApiError } from '../../utils/formatApiError';

const { Title, Paragraph, Text } = Typography;

export const VAULT_FIELDS_TO_HIDE = [
    'Id',
    'StudyHash',
    'IsOptedOut',
    'OptOutRequestedAt',
    'OptOutSource',
    'OrganisationId',
    'TenantPatientId',
    'SubStudyIds',
    'CreatedAt',
    'UpdatedAt',
];

export const VaultHelpPanel = ({ t }) => (
    <>
        <Title level={4}>{t('Identifier Vault')}</Title>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {t('Cross-organisation participant identity (protocol §8). Personal identifiers live here separately from research data.')}
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>{t('Study Hash')}</Text>
            <br />
            <Text type="secondary">{t('Deterministic SHA-256 of NHS/CHI + date of birth + per-participant salt. One hash per person across hospitals.')}</Text>
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>{t('Site enrolment')}</Text>
            <br />
            <Text type="secondary">{t('Organisation, Site-Study ID and tenant patient link live on the participant row.')}</Text>
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>{t('Sub-studies')}</Text>
            <br />
            <Text type="secondary">{t('Manage future study codes via Sub-studies. Enrol from the participant page. Filter the list by Is Opted Out for withdrawn rows.')}</Text>
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>{t('Withdrawal')}</Text>
            <br />
            <Text type="secondary">{t('Record withdrawal erases personal identifiers but keeps Salt and Study Hash so the person cannot be re-added (§5.3).')}</Text>
        </Paragraph>
    </>
);

const BackfillButton = ({ onDone }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { loading, success, result, error } = useSelector((s) => s.vaultPatientBackfill || {});

    useEffect(() => {
        if (error) showApiError(error, t);
    }, [error, t]);

    useEffect(() => {
        if (!success) return;
        message.success(result?.message || t('Study Hashes recomputed'));
        dispatch({ type: VAULT_PATIENT_BACKFILL_RESET });
        if (onDone) onDone();
    }, [success]);

    return (
        <Popconfirm
            title={t('Recompute Study Hashes')}
            description={t('Recomputes hashes where NHS/CHI and DOB exist. Skips withdrawn/empty rows. Safe to re-run.')}
            okText={t('Run')}
            cancelText={t('Cancel')}
            onConfirm={() => dispatch(backfillStudyHashes())}
        >
            <Button size="small" icon={<ReloadOutlined />} loading={loading}>
                {t('Backfill Study Hashes')}
            </Button>
        </Popconfirm>
    );
};

// List only — View/Edit is /identifier_vault/:participantId (no create; participants are minted on Patient save).
const IdentifierVaultScreen = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [subStudiesOpen, setSubStudiesOpen] = useState(false);

    return (
        <>
            <EntityManagementScreen
                entityName="VaultPatient"
                beautifulName={t('Participant')}
                icon={<FaVault size={24} />}
                fieldsToHide={VAULT_FIELDS_TO_HIDE}
                enableCreate={false}
                initialListFilter={{ page: 1, limit: 10, filter: 'patientIdentifier', query: '' }}
                helpContent={<VaultHelpPanel t={t} />}
                onEditClick={(id) => navigate(`/identifier_vault/${id}`)}
                extraHeaderActions={(refreshList) => (
                    <Space>
                       
                        <BackfillButton onDone={refreshList} />
                        <Button size="small" icon={<BsFillBinocularsFill />} onClick={() => setSubStudiesOpen(true)} >
                            {t('Sub-studies')}
                        </Button>
                    </Space>
                )}
            />
            <VaultSubStudyManagementDrawer open={subStudiesOpen} onClose={() => setSubStudiesOpen(false)} />
        </>
    );
};

export default IdentifierVaultScreen;
